import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFeedback } from "../../../api/feedback.service";
import { getLanguageDetails } from "../../../api/HorecaWidget.service";
import {
  setBasicLanguages,
  updateDefaultLanguage,
  updateFeedbackDetails,
  updateLanguageData,
} from "../../../store/slice/feedback.slice";
import { updateCss } from "../../../store/slice/widgetSetting.slice";
import { fout, meld } from '../../../lib/logboek';

/**
 * Custom hook for fetching and managing feedback data
 */
export const useFeedbackData = (secretkey, key, moduleID, queryParams) => {
  const dispatch = useDispatch();
  const { defaultLanguage } = useSelector((s) => s.feedback);

  const [loading, setLoading] = useState(false);
  const [langLoading, setLangLoading] = useState(false);

  const fetchLanguageDetails = useCallback(async () => {
    if (!key) return;

    setLangLoading(true);
    try {
      const response = await getLanguageDetails(key);
      if (!response || response.status !== 200) return;

      const langParam = queryParams.get("lang");
      const isValid =
        langParam && !["null", "undefined", ""].includes(langParam.trim());
      const languageList = response?.data || [];
      
      let selected = isValid ? langParam : defaultLanguage;
      if (!selected) {
        selected = languageList.find((i) => i?.defaultLanguage)?.langcode;
      }
      
      if (selected) dispatch(updateDefaultLanguage(selected));
      dispatch(updateLanguageData(languageList));
      
      const resType = queryParams.get("resType");
      localStorage.setItem(
        "widget",
        JSON.stringify({ key, lang: selected, resType })
      );
    } catch (error) {
      fout("Language fetch error:", error?.message);
    } finally {
      setLangLoading(false);
    }
  }, [key, defaultLanguage, dispatch, queryParams]);

  const fetchFeedbackDetails = useCallback(async () => {
    if (!secretkey || !defaultLanguage) return;

    setLoading(true);
    try {
      const response = await getFeedback(secretkey, defaultLanguage, moduleID);
      meld("Feedback response:", response, secretkey, defaultLanguage, moduleID);
      
      if (!response) return;

      const result = response?.data?.data?.basicLanguages?.reduce(
        (acc, item) => {
          const tag = item.tag.replace(/[{}]/g, "");
          acc[tag] = item.widgetTranslateLanguages[0]?.name || "";
          return acc;
        },
        {}
      );

      dispatch(updateCss(response?.data?.settings?.cssGroup));
      dispatch(setBasicLanguages(result));
      dispatch(updateFeedbackDetails(response?.data?.data));
    } catch (error) {
      fout("Feedback fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [secretkey, defaultLanguage, moduleID, dispatch]);

  useEffect(() => {
    fetchLanguageDetails();
  }, [fetchLanguageDetails]);

  useEffect(() => {
    fetchFeedbackDetails();
  }, [fetchFeedbackDetails]);

  return { loading, langLoading };
};
